import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-[color:var(--color-on-primary)] hover:bg-primary-600 active:bg-primary-700 focus-visible:ring-primary-soft',
  secondary:
    'bg-surface text-ink border border-border-soft hover:border-border-strong hover:bg-surface-sunken focus-visible:ring-primary-soft',
  ghost:
    'bg-transparent text-ink-secondary hover:bg-primary-soft hover:text-primary focus-visible:ring-primary-soft',
  danger:
    'bg-error text-white hover:bg-error-600 focus-visible:ring-error/30',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
  md: 'h-10 w-10 [&>svg]:h-5 [&>svg]:w-5',
  lg: 'h-12 w-12 [&>svg]:h-5 [&>svg]:w-5',
};

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  label: string;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { className, variant = 'ghost', size = 'md', label, children, type = 'button', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-sm',
          'transition-[background-color,color,border-color,transform] duration-fast ease-enter',
          'focus-visible:outline-none focus-visible:ring-4',
          'disabled:opacity-50 disabled:pointer-events-none',
          'active:scale-[0.96] motion-reduce:active:scale-100',
          variants[variant],
          sizes[size],
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
